
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { DiscoverListFilterModel } from '../../../../api/mentions/model/discover-list-filter.model';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { Observable, Subscription, filter, iif, map, of, switchMap, tap } from 'rxjs';
import { DistributionService } from '../../../../Common/TargetingShared/services/distribution.service';
import { DistributionStatus } from '../../../../Common/TargetingShared/enum/distribution-status.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { DiscoverListTypeEnum } from '../../../../api/mentions/enum/discover-list-type.enum';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingSearchList } from '../../../../Discover/Targeting/models/targeting-search-list.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';

import { SourcePickerService } from '../../../../Discover/Targeting/services/source-picker-service';
import { FilterGroupModel } from '../../../../Common/Filter/models/filter-group.model';
import { ReachFilterModel } from '../../../../Common/TargetingShared/model/reach-filter.model';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { TargetingQueryApi } from '@api/targeting/model/targeting-search.api.model';
import { TargetingSearchesApiService } from '@api/targeting/targeting-searches.api.service';
import { deepCompare } from '@Core/utils';
import { TargetingSearchApi } from '../../../../api/targeting/model/targeting-search.api.model';


@Component({
    selector: 'agpr-discover-list-picker',
    templateUrl: './discover-list-picker.component.html',
    styleUrls: ['./discover-list-picker.component.scss'],
    providers: [
        SourcePickerService,
        {
            provide: FilterService,
            useExisting: SourcePickerService
        },
        getProvider(DiscoverListPickerComponent)
    ],
    standalone: false
})
export class DiscoverListPickerComponent extends ControlValueAccessorModel<DiscoverListFilterModel> {
    @Input() id: string = 'discoverListPicker';
    @Output() isDirty: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    public readonly messageSeverityEnum = messageSeverityEnum;
    discoverListTypeOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Monitor.none', DiscoverListTypeEnum.none),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.targetingList}`, DiscoverListTypeEnum.targetingList),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.targetingSearch}`, DiscoverListTypeEnum.targetingSearch),
        new DropdownOptionModel(`Discover.listSource.${DiscoverListTypeEnum.distribution}`, DiscoverListTypeEnum.distribution),
    ];

    source$: Observable<any[]> = of(null);
    selectedList: any;
    selectedListType: DiscoverListTypeEnum = DiscoverListTypeEnum.none;
    searchAssociatedOutlets: boolean = false;
    chips: ChipModel[] = [];
    viewSourceTooltip: string;
    chipsVisible: boolean = false;
    cachedFilterSearchContext: TargetingQueryApi = null;
    subscriptions: Subscription[] = [];

    readonly discoverListTypes = DiscoverListTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;
    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public sourcePickerService: SourcePickerService,
        public targetingSearchesService: TargetingSearchesService,
        public targetingSearchesApiService: TargetingSearchesApiService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public targetingListService: TargetingListService,
        public distributionService: DistributionService
    ) {
        super();
        this.subscriptions.push(
            this.sourcePickerService.getItemCount().subscribe(
                total => {
                    if (total === null) {
                        return;
                    }
                    if (this.value) {
                        this.value.contactCount = total;
                        this.value.currentFilterSearchContext = this.sourcePickerService.getCurrentFilterSearchContext();

                        this.isDirty.emit(this.checkDirty());
                        this.onModelChange(this.value);
                    }

                }
            ),
            this.sourcePickerService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(_filter => {
                    if (_filter instanceof FilterGroupModel) {
                        return _filter.children.find(x => x.chips && x.chips.length > 0);
                    } else if (_filter instanceof ReachFilterModel) {
                        return _filter.hasValueSet();
                    } else {
                        return _filter.chips && _filter.chips.length > 0;
                    }
                });
            })
        );


    }

    writeValue(value: DiscoverListFilterModel): void {
        this.value = value;
        if (!value) {
            this.searchAssociatedOutlets = false;
            this.selectedListType = DiscoverListTypeEnum.none;
            this.selectedList = null;
            return;
        }

        this.selectedListType = this.discoverListTypeOptions.find(x => x.value === this.value.discoverListType)?.value;
        this.viewSourceTooltip = `Discover.listPicker.viewSource.${this.selectedListType}`;
        this.setAutocompleteSuggestions();
        this.searchAssociatedOutlets = this.value.useEmploymentOutletId;

        if (this.value.discoverList?.include[0]) {
            if (this.value.filterSearchId) {
                /** @TODO - consider refactoring to use TargetingSearch and not the api */
                this.targetingSearchesApiService.getById(this.value.filterSearchId).pipe(
                    tap(
                        search => {
                            if (search) {
                                this.sourcePickerService.populateFiltersFromSearch(new TargetingSearch(search));
                                this.value.currentFilterSearchContext = search;
                                this.cachedFilterSearchContext = search;
                            }
                        }
                    ),
                    switchMap(() => {
                        return this.source$;
                    })
                ).subscribe(
                    source => {
                        if (source) {
                            this.selectedList = source.find(x => x.id === this.value.discoverList.include[0]);
                            this.selectSource(this.selectedList, false);
                        }
                    }
                );
            } else {
                this.source$.subscribe(source => {
                    if (source) {
                        this.selectedList = source.find(x => x.id === this.value.discoverList.include[0]);
                        this.selectSource(this.selectedList);
                    }
                });
            }
        }
    }

    selectSource(source: TargetingListList | TargetingSearchList | DistributionSummaryModel, clearFilter: boolean = true) {
        if (source?.id) {
            this.value.discoverList = {
                include: [source.id],
                exclude: []
            };
        } else {
            this.value.discoverList = {
                include: [],
                exclude: []
            };
            this.value.contactCount = null;
            this.value.currentFilterSearchContext = null;
        }
        if (clearFilter) {
            this.sourcePickerService.clearFilters();
        }

        if (source instanceof TargetingListList) {
            this.sourcePickerService.setActiveList(source, false);
        } else if (source instanceof TargetingSearchList) {
            this.sourcePickerService.setActiveSearch(source, false);
        } else if (source instanceof DistributionSummaryModel) {
            this.sourcePickerService.setActiveDistribution(source, false);
        }

        this.onModelChange(this.value);
    }

    selectListType() {
        if (this.selectedListType === DiscoverListTypeEnum.none) {
            this.value = null;
        } else {
            this.value = new DiscoverListFilterModel();
            this.value.useEmploymentOutletId = false;
            this.value.discoverListType = this.selectedListType;
        }

        this.viewSourceTooltip = `Discover.listPicker.viewSource.${this.selectedListType}`;
        this.selectedList = null;

        this.setAutocompleteSuggestions();
        this.sourcePickerService.clearFilters();
        this.onModelChange(this.value);
    }

    setDistributionRecipientSearchMode() {
        this.value.useEmploymentOutletId = this.searchAssociatedOutlets;
        this.onModelChange(this.value);
    }


    viewSource() {
        switch (this.selectedListType) {
            case DiscoverListTypeEnum.targetingList:
                window.open(this.appBaseHref + `Discover/targetinglists/list/${this.selectedList?.id}/results`, '_blank');
                break;
            case DiscoverListTypeEnum.targetingSearch:
                window.open(this.appBaseHref + `Discover/targetingsearches/search/${this.selectedList?.id}/results`, '_blank');
                break;
            case DiscoverListTypeEnum.distribution:
                if (this.selectedList.status === DistributionStatus.draft) {
                    window.open(this.appBaseHref + `Discover/distributions/distribution/${this.selectedList?.id}?step=editRecipients`, '_blank');
                } else {
                    window.open(this.appBaseHref + `Discover/distributions/distribution/${this.selectedList?.id}/report`, '_blank');
                }
                break;
        }
    }

    setAutocompleteSuggestions() {
        switch (this.selectedListType) {
            case DiscoverListTypeEnum.targetingList:
                this.source$ = this.targetingListService.all$;
                break;
            case DiscoverListTypeEnum.targetingSearch:
                this.source$ = this.targetingSearchesService.all$;
                break;
            case DiscoverListTypeEnum.distribution:
                this.source$ = this.distributionService.getDistributionSummaries().pipe(
                    filter(x => !!x)
                );
                break;
            default:
                this.source$ = of(null);
                break;
        }
    }

    checkDirty(): boolean {
        const compareFieldsToIgnore = ['name', 'id', 'searchMode'];
        const cachedSearchContext = new TargetingSearch(this.cachedFilterSearchContext);

        for (const field of compareFieldsToIgnore) {
            delete cachedSearchContext[field];
        }

        // special case for status filter field
        const currentBadgeFilters = this.value.currentFilterSearchContext?.query?.badgeFilters;
        const cachedBadgeFilters = (this.cachedFilterSearchContext as TargetingSearchApi)?.query?.badgeFilters;
        if (currentBadgeFilters && cachedBadgeFilters && !deepCompare(currentBadgeFilters, cachedBadgeFilters)) {
            return true;
        }
        if (this.value.currentFilterSearchContext?.query?.hasActiveNotices !== this.cachedFilterSearchContext?.hasActiveNotices
            || this.value.currentFilterSearchContext?.query?.ownershipTypes !== this.cachedFilterSearchContext?.ownershipTypes
        ) {
            return true;
        }

        return !new TargetingSearch(this.value.currentFilterSearchContext).equals(cachedSearchContext);
    }

}
